/**
 * Navigation safety (spec §14). Defense-in-depth only — the backend Auth Client
 * Registry is the source of truth for allowed product origins and return routes.
 *
 * `nextUrl` returned by the backend is already sanitized, but we re-validate
 * before any browser redirect. We never redirect using the raw `return_route`
 * taken from the current browser URL.
 */
import { env } from './env';

/** App-relative portal paths that are always safe to navigate to internally. */
const ALLOWED_PORTAL_PREFIXES = ['/account', '/onboarding', '/login', '/auth/complete', '/error'];

/**
 * Product origins that the portal is allowed to redirect to after login.
 * The backend remains authoritative; this is a minimal local allow-list.
 */
const ALLOWED_PRODUCT_ORIGINS: readonly string[] = [env.portalOrigin];

/** Rejects backslashes and whitespace/control chars used to smuggle a host. */
function hasUnsafeChars(value: string): boolean {
  if (value.includes('\\')) return true;
  for (const ch of value) {
    if (ch.charCodeAt(0) <= 0x20) return true;
  }
  return false;
}

function isSafeRelativePath(value: string): boolean {
  // Must start with exactly one "/" and not be protocol-relative ("//host").
  if (!value.startsWith('/') || value.startsWith('//')) return false;
  if (hasUnsafeChars(value)) return false;
  if (value === '/') return true;
  return ALLOWED_PORTAL_PREFIXES.some(
    (prefix) =>
      value === prefix || value.startsWith(`${prefix}/`) || value.startsWith(`${prefix}?`),
  );
}

function isAllowedAbsoluteUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  // Only https, and never with embedded userinfo (https://acct@attacker).
  if (url.protocol !== 'https:') return false;
  if (url.username !== '' || url.password !== '') return false;
  return ALLOWED_PRODUCT_ORIGINS.includes(url.origin);
}

/**
 * Returns true if `target` is a portal-relative path or an allow-listed product
 * URL. Blocks `javascript:`, `data:`, `file:`, `//host`, and userinfo tricks.
 */
export function isPortalPathOrAllowedProductUrl(target: string | null | undefined): boolean {
  if (typeof target !== 'string' || target.length === 0) return false;
  const value = target.trim();
  if (value.length === 0) return false;
  if (value.startsWith('/')) return isSafeRelativePath(value);
  return isAllowedAbsoluteUrl(value);
}

/** Returns a safe navigation target or a fallback. Never throws. */
export function toSafeTarget(target: string | null | undefined, fallback: string): string {
  return isPortalPathOrAllowedProductUrl(target) ? (target as string) : fallback;
}
